import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supportApi } from "../../../api/supportApi";
import { useNotifications } from "../../../context/NotificationContext";
import Modal from "../../../components/common/modals/Modal";
import { toast } from "react-toastify";
import {
  formatChatDateTime,
  formatConversationTimestamp,
} from "../../../utils/chatDateTime";
import "./AdminSupportTickets.css";

const QUICK_REPLIES = [
  {
    label: "We’re looking into this",
    message:
      "Thanks for contacting Toora Flex. We’re looking into this and will update you as soon as we have more information.",
  },
  {
    label: "Need more details",
    message:
      "Thanks for reaching out. Could you please share a little more detail so we can help you resolve this?",
  },
  {
    label: "We’re here to help",
    message:
      "Hello! We’ve received your message and our team is here to help. Please let us know if there’s anything else you’d like to add.",
  },
];

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("en-ZA", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const getStatus = (status) =>
  String(status || "open").replace("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

const getTicketActivityTime = (ticket) =>
  ticket.LastMessageAt || ticket.UpdatedAt || ticket.CreatedAt;

const getTimestamp = (value) => {
  const timestamp = Date.parse(value || "");
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const sortTicketsByLatestMessage = (items = []) =>
  [...items].sort(
    (first, second) =>
      getTimestamp(getTicketActivityTime(second)) -
      getTimestamp(getTicketActivityTime(first)),
  );

const MAX_BULK_SELECTION = 100;

const AdminSupportTickets = () => {
  const { socket } = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams();
  const ticketIdFromUrl = searchParams.get("ticket");
  const [tickets, setTickets] = useState([]);
  const [archives, setArchives] = useState([]);
  const [viewMode, setViewMode] = useState("live");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [processingBulkAction, setProcessingBulkAction] = useState(false);
  const [confirmAction, setConfirmAction] = useState("");
  const [exportPassword, setExportPassword] = useState("");
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [savingRetention, setSavingRetention] = useState(false);
  const [retention, setRetention] = useState({ amount: 30, unit: "days" });
  const [error, setError] = useState("");
  const messageListRef = React.useRef(null);
  const loadVersionRef = React.useRef(0);
  const archiveSearch = viewMode === "archived" ? search : "";
  const archiveFrom = viewMode === "archived" ? dateFrom : "";
  const archiveTo = viewMode === "archived" ? dateTo : "";

  const loadTickets = useCallback(async ({ silent = false } = {}) => {
    const requestVersion = ++loadVersionRef.current;
    if (!silent) setLoading(true);
    try {
      const response = viewMode === "live"
        ? await supportApi.getAdminTickets()
        : await supportApi.getAdminArchives({
            search: archiveSearch,
            from: archiveFrom,
            to: archiveTo,
          });
      if (requestVersion !== loadVersionRef.current) return;
      if (viewMode === "live") {
        setTickets(sortTicketsByLatestMessage(response.data || []));
      }
      else setArchives(response.data || []);
      setError("");
    } catch (requestError) {
      if (requestVersion !== loadVersionRef.current) return;
      setError(
        requestError.response?.data?.error ||
          `Could not load ${viewMode === "live" ? "support conversations" : "archived conversations"}.`,
      );
    } finally {
      if (requestVersion === loadVersionRef.current) setLoading(false);
    }
  }, [archiveFrom, archiveSearch, archiveTo, viewMode]);

  const openTicket = useCallback(
    async (ticketId) => {
      try {
        const response = await supportApi.getAdminTicket(ticketId);
        setSelectedTicket(response.data);
        setSearchParams({ ticket: ticketId }, { replace: true });
      } catch (requestError) {
        setError(
          requestError.response?.data?.error ||
            "Could not open this support ticket.",
        );
      }
    },
    [setSearchParams],
  );

  useEffect(() => {
    const timeout = setTimeout(loadTickets, viewMode === "archived" ? 250 : 0);
    return () => clearTimeout(timeout);
  }, [loadTickets]);

  useEffect(() => {
    supportApi
      .getGuestRetentionPolicy()
      .then((response) => setRetention(response.data))
      .catch((requestError) => {
        setError(
          requestError.response?.data?.error ||
            "Could not load guest chat retention settings.",
        );
      });
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    const refreshTickets = ({ ticketId } = {}) => {
      if (ticketId && viewMode === "live") {
        setTickets((current) =>
          sortTicketsByLatestMessage(
            current.map((ticket) =>
              ticket.SupportTicketID === ticketId
                ? { ...ticket, LastMessageAt: new Date().toISOString() }
                : ticket,
            ),
          ),
        );
      }
      loadTickets({ silent: true });
      if (
        viewMode === "live" &&
        ticketId &&
        selectedTicket?.SupportTicketID === ticketId
      ) {
        openTicket(ticketId);
      }
    };
    const handleDeletedTicket = ({ ticketId } = {}) => {
      loadTickets();
      if (
        viewMode === "live" &&
        ticketId &&
        selectedTicket?.SupportTicketID === ticketId
      ) {
        setSelectedTicket(null);
        setSearchParams({}, { replace: true });
      }
    };
    const handleArchiveUpdated = ({ archiveId } = {}) => {
      loadTickets();
      if (archiveId && selectedTicket?.ArchiveID === archiveId) {
        setSelectedTicket(null);
      }
    };
    socket.on("support:updated", refreshTickets);
    socket.on("support:deleted", handleDeletedTicket);
    socket.on("support:archive-updated", handleArchiveUpdated);
    return () => {
      socket.off("support:updated", refreshTickets);
      socket.off("support:deleted", handleDeletedTicket);
      socket.off("support:archive-updated", handleArchiveUpdated);
    };
  }, [
    loadTickets,
    openTicket,
    selectedTicket?.SupportTicketID,
    setSearchParams,
    socket,
    viewMode,
  ]);

  useEffect(() => {
    if (!ticketIdFromUrl) return;
    openTicket(ticketIdFromUrl);
  }, [ticketIdFromUrl, openTicket]);

  useEffect(() => {
    messageListRef.current?.scrollTo({
      top: messageListRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [selectedTicket?.Messages?.length]);

  const filteredTickets = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const rows = viewMode === "live" ? tickets : archives;
    return rows.filter((ticket) => {
      const statusMatches =
        viewMode === "archived" ||
        statusFilter === "all" ||
        (statusFilter === "open"
          ? ["open", "in_progress"].includes(ticket.Status)
          : ["closed", "resolved"].includes(ticket.Status));
      const searchMatches =
        !normalizedSearch ||
        [
          ticket.TicketNumber,
          ticket.Subject,
          ticket.ContactName,
          ticket.ContactEmail,
          ticket.LastMessage,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch));
      return statusMatches && searchMatches;
    });
  }, [archives, tickets, viewMode, statusFilter, search]);

  const attentionCount = tickets.filter((ticket) =>
    ["open", "in_progress"].includes(ticket.Status),
  ).length;
  const closedCount = tickets.filter((ticket) =>
    ["closed", "resolved"].includes(ticket.Status),
  ).length;

  const refreshSelected = async () => {
    if (!selectedTicket || selectedTicket.Archived) return;
    const response = await supportApi.getAdminTicket(
      selectedTicket.SupportTicketID,
    );
    setSelectedTicket(response.data);
  };

  const sendReply = async (event) => {
    event.preventDefault();
    if (!selectedTicket || !reply.trim()) return;
    setSending(true);
    setError("");
    try {
      const messageText = reply.trim();
      await supportApi.sendAdminMessage(
        selectedTicket.SupportTicketID,
        messageText,
      );
      const sentAt = new Date().toISOString();
      setTickets((current) =>
        sortTicketsByLatestMessage(
          current.map((ticket) =>
            ticket.SupportTicketID === selectedTicket.SupportTicketID
              ? {
                  ...ticket,
                  LastMessage: messageText,
                  LastMessageAt: sentAt,
                  LastSenderType: "admin",
                }
              : ticket,
          ),
        ),
      );
      setReply("");
      await refreshSelected();
      await loadTickets({ silent: true });
    } catch (requestError) {
      setError(
        requestError.response?.data?.error || "Could not send your reply.",
      );
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (status) => {
    if (!selectedTicket) return;
    setError("");
    try {
      await supportApi.updateAdminTicketStatus(
        selectedTicket.SupportTicketID,
        status,
      );
      toast.success(
        status === "closed"
          ? "Conversation ended. The client has been notified."
          : `Ticket marked ${status.replace("_", " ")}.`,
      );
      await refreshSelected();
      await loadTickets();
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          "Could not update the ticket status.",
      );
    }
  };

  const saveRetention = async (event) => {
    event.preventDefault();
    setSavingRetention(true);
    setError("");
    try {
      const response = await supportApi.updateGuestRetentionPolicy(retention);
      setRetention(response.data);
      toast.success("Guest chat retention settings saved.");
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          "Could not save guest chat retention settings.",
      );
    } finally {
      setSavingRetention(false);
    }
  };

  const deleteSelectedTicket = () => {
    if (!selectedTicket) return;
    setConfirmAction("delete-selected");
  };

  const openArchive = async (archiveId) => {
    try {
      const response = await supportApi.getAdminArchive(archiveId);
      setSelectedTicket(response.data);
      setSearchParams({}, { replace: true });
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          "Could not open this archived conversation.",
      );
    }
  };

  const handleModeChange = (mode) => {
    setViewMode(mode);
    setLoading(true);
    setSelectedTicket(null);
    setSelectedIds([]);
    setSearchParams({}, { replace: true });
    setError("");
  };

  const visibleIds = filteredTickets.map((ticket) =>
    viewMode === "live" ? ticket.SupportTicketID : ticket.ArchiveID,
  );
  const selectableIds = visibleIds.slice(0, MAX_BULK_SELECTION);
  const allVisibleSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.includes(id));

  const toggleSelected = (id) => {
    if (
      !selectedIds.includes(id) &&
      selectedIds.length >= MAX_BULK_SELECTION
    ) {
      setError(`Select no more than ${MAX_BULK_SELECTION} conversations at a time.`);
      return;
    }
    setSelectedIds((current) => {
      if (current.includes(id)) {
        return current.filter((selectedId) => selectedId !== id);
      }
      return [...current, id];
    });
  };

  const toggleSelectAll = () => {
    if (visibleIds.length > MAX_BULK_SELECTION) {
      toast.info(`Select all applies to the first ${MAX_BULK_SELECTION} visible conversations.`);
    }
    setSelectedIds((current) => {
      if (allVisibleSelected) {
        return current.filter((id) => !selectableIds.includes(id));
      }
      return [...new Set([...current, ...selectableIds])].slice(
        0,
        MAX_BULK_SELECTION,
      );
    });
  };

  const performDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      if (confirmAction === "delete-selected" && selectedTicket?.Archived) {
        await supportApi.deleteAdminArchives([selectedTicket.ArchiveID]);
        setArchives((current) =>
          current.filter((item) => item.ArchiveID !== selectedTicket.ArchiveID),
        );
      } else if (confirmAction === "delete-selected" && selectedTicket) {
        await supportApi.deleteAdminTicket(selectedTicket.SupportTicketID);
      } else if (viewMode === "live") {
        await supportApi.deleteAdminTickets(selectedIds);
      } else {
        await supportApi.deleteAdminArchives(selectedIds);
      }
      setSelectedTicket(null);
      setSelectedIds([]);
      setSearchParams({}, { replace: true });
      setConfirmAction("");
      toast.success("Selected conversation(s) deleted.");
      await loadTickets();
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          "Could not delete this conversation.",
      );
    } finally {
      setDeleting(false);
    }
  };

  const archiveSelected = async () => {
    setProcessingBulkAction(true);
    setError("");
    try {
      await supportApi.archiveAdminTickets(selectedIds);
      setSelectedIds([]);
      toast.success(`${selectedIds.length} conversation(s) moved to the archive.`);
      await loadTickets();
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          "Could not archive the selected conversations.",
      );
    } finally {
      setProcessingBulkAction(false);
    }
  };

  const confirmSelectedArchive = () => {
    if (!selectedIds.length) return;
    setConfirmAction("archive-bulk");
  };

  const confirmSelectedDelete = () => {
    if (!selectedIds.length) return;
    setConfirmAction("delete-bulk");
  };

  const runConfirmedAction = async () => {
    if (confirmAction === "archive-bulk") {
      setConfirmAction("");
      await archiveSelected();
      return;
    }
    await performDelete();
  };

  const exportSelected = async () => {
    setProcessingBulkAction(true);
    setError("");
    try {
      const randomBytes = new Uint8Array(32);
      window.crypto.getRandomValues(randomBytes);
      const password = window.btoa(
        String.fromCharCode(...randomBytes),
      ).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
      const file = await supportApi.exportAdminConversations({
        ticketIds: viewMode === "live" ? selectedIds : [],
        archiveIds: viewMode === "archived" ? selectedIds : [],
        password,
      });
      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = "toora-flex-conversations.zip";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      setExportPassword(password);
      setPasswordCopied(false);
    } catch (requestError) {
      let message =
        requestError.response?.data?.error ||
        "Could not export the selected conversations.";
      if (requestError.response?.data instanceof Blob) {
        try {
          const payload = JSON.parse(await requestError.response.data.text());
          message = payload.error || message;
        } catch {
          message = "Could not export the selected conversations.";
        }
      }
      setError(message);
    } finally {
      setProcessingBulkAction(false);
    }
  };

  const copyExportPassword = async () => {
    try {
      await navigator.clipboard.writeText(exportPassword);
      setPasswordCopied(true);
    } catch {
      setError("Could not copy the export password. Please copy it manually.");
    }
  };

  const changeTicket = (ticket) => openTicket(ticket.SupportTicketID);

  return (
    <main className="admin-support-page">
      <header className="admin-support-header">
        <div>
          <span className="admin-support-eyebrow">CUSTOMER CARE</span>
          <h1>Communication</h1>
          <p>Review and respond to questions sent from your storefront.</p>
        </div>
        <button type="button" className="admin-support-refresh" onClick={loadTickets}>
          <i className="fas fa-rotate-right" /> Refresh
        </button>
      </header>

      {error && <div className="admin-support-alert error">{error}</div>}
      <section className="admin-support-stats" aria-label="Ticket totals">
        <article>
          <span>{viewMode === "live" ? "Total requests" : "Archived conversations"}</span>
          <strong>{viewMode === "live" ? tickets.length : archives.length}</strong>
        </article>
        <article><span>Needs attention</span><strong>{attentionCount}</strong></article>
        <article><span>Resolved or closed</span><strong>{closedCount}</strong></article>
      </section>

      <form className="admin-support-retention" onSubmit={saveRetention}>
        <div>
          <strong>Guest chat retention</strong>
          <span>
            Guest conversations and archived guest copies are automatically
            deleted after this period. Client-account conversations are not affected.
          </span>
        </div>
        <label className="admin-support-retention-field">
          <span>Keep for</span>
          <input
            type="number"
            min="1"
            max="3650"
            required
            value={retention.amount}
            onChange={(event) =>
              setRetention((current) => ({
                ...current,
                amount: event.target.value,
              }))
            }
          />
        </label>
        <label className="admin-support-retention-field">
          <span>Unit</span>
          <select
            value={retention.unit}
            onChange={(event) =>
              setRetention((current) => ({
                ...current,
                unit: event.target.value,
              }))
            }
          >
            <option value="days">Days</option>
            <option value="months">Months</option>
            <option value="years">Years</option>
          </select>
        </label>
        <button type="submit" disabled={savingRetention}>
          {savingRetention ? "Saving…" : "Save retention"}
        </button>
      </form>

      <section className="admin-support-workspace">
        <aside className={`admin-support-list ${selectedTicket ? "mobile-hide" : ""}`}>
          <div className="admin-support-view-tabs" role="tablist" aria-label="Conversation views">
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "live"}
              className={viewMode === "live" ? "active" : ""}
              onClick={() => handleModeChange("live")}
            >
              Conversations <span>{tickets.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "archived"}
              className={viewMode === "archived" ? "active" : ""}
              onClick={() => handleModeChange("archived")}
            >
              Archive vault <span>{archives.length}</span>
            </button>
          </div>
          <div className="admin-support-filters">
            <label className="admin-support-search">
              <i className="fas fa-magnifying-glass" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={viewMode === "live" ? "Search requests" : "Search name, date, ticket or subject"}
              />
            </label>
            {viewMode === "live" ? (
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                aria-label="Filter support tickets"
              >
                <option value="all">All</option>
                <option value="open">Needs attention</option>
                <option value="closed">Resolved or closed</option>
              </select>
            ) : (
              <div className="admin-support-date-filters">
                <label>
                  <span>From</span>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(event) => setDateFrom(event.target.value)}
                  />
                </label>
                <label>
                  <span>To</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(event) => setDateTo(event.target.value)}
                  />
                </label>
              </div>
            )}
          </div>
          <div className="admin-support-selectionbar">
            <label>
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleSelectAll}
                aria-label="Select all visible conversations"
              />
              Select all
            </label>
            <span>{selectedIds.length} selected</span>
            {selectedIds.length > 0 && (
              <div className="admin-support-bulk-actions">
                {viewMode === "live" && (
                  <button
                    type="button"
                    onClick={confirmSelectedArchive}
                    disabled={processingBulkAction}
                  >
                    Archive
                  </button>
                )}
                <button
                  type="button"
                  onClick={exportSelected}
                  disabled={processingBulkAction}
                >
                  Export ZIP
                </button>
                <button
                  type="button"
                  className="danger"
                  onClick={confirmSelectedDelete}
                  disabled={processingBulkAction}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
          <div className="admin-support-ticket-list">
            {loading ? (
              <div className="admin-support-empty">Loading requests…</div>
            ) : filteredTickets.length === 0 ? (
              <div className="admin-support-empty">
                <i className="far fa-comment-dots" />
                <strong>No tickets found</strong>
                <span>New customer requests will appear here.</span>
              </div>
            ) : (
              filteredTickets.map((ticket) => {
                const itemId = viewMode === "live"
                  ? ticket.SupportTicketID
                  : ticket.ArchiveID;
                return (
                  <div className="admin-support-ticket-row" key={itemId}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(itemId)}
                      onChange={() => toggleSelected(itemId)}
                      aria-label={`Select conversation ${ticket.TicketNumber || ""}`}
                    />
                    <button
                      type="button"
                      className={`admin-support-ticket ${selectedTicket?.SupportTicketID === itemId || selectedTicket?.ArchiveID === itemId ? "selected" : ""}`}
                      onClick={() =>
                        viewMode === "live"
                          ? changeTicket(ticket)
                          : openArchive(itemId)
                      }
                    >
                      <span className="admin-support-avatar">🙂</span>
                      <span className="admin-support-ticket-copy">
                        <span className="admin-support-ticket-heading">
                          <strong>{ticket.ContactName || ticket.ContactEmail || "Website visitor"}</strong>
                          {viewMode === "live" && (
                            <time
                              dateTime={ticket.LastMessageAt || ticket.CreatedAt}
                              title={formatChatDateTime(ticket.LastMessageAt || ticket.CreatedAt)}
                            >
                              {formatConversationTimestamp(ticket.LastMessageAt || ticket.CreatedAt)}
                            </time>
                          )}
                        </span>
                        <span>{ticket.Subject} · {ticket.TicketNumber}</span>
                        <small>
                          {viewMode === "archived"
                            ? `Archived ${formatDate(ticket.ArchivedAt)}`
                            : ticket.LastMessage || "Conversation started"}
                        </small>
                      </span>
                      <span className="admin-support-ticket-meta">
                        {viewMode === "live" ? (
                          <span className={`admin-support-status ${ticket.Status}`}>
                            {getStatus(ticket.Status)}
                          </span>
                        ) : (
                          <span className="admin-support-archived-label">Archived</span>
                        )}
                      </span>
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        <section className={`admin-support-conversation ${!selectedTicket ? "mobile-hide" : ""}`}>
          {selectedTicket ? (
            <>
              <header className="admin-support-conversation-header">
                <button
                  type="button"
                  className="admin-support-back"
                  onClick={() => {
                    setSelectedTicket(null);
                    setSearchParams({}, { replace: true });
                  }}
                  aria-label="Back to support requests"
                >
                  <i className="fas fa-arrow-left" />
                </button>
                <span className="admin-support-avatar admin-support-avatar-large">🙂</span>
                <div className="admin-support-contact">
                  <strong>{selectedTicket.ContactName || "Website visitor"}</strong>
                  <span>{selectedTicket.ContactEmail} · {selectedTicket.TicketNumber}</span>
                </div>
                {selectedTicket.Archived ? (
                  <span className="admin-support-archived-label">Archived copy</span>
                ) : (
                  <>
                    <span className={`admin-support-status ${selectedTicket.Status}`}>
                      {getStatus(selectedTicket.Status)}
                    </span>
                    {["closed", "resolved"].includes(selectedTicket.Status) ? (
                      selectedTicket.AutoClosedAt ? (
                        <span className="admin-support-expired">Expired</span>
                      ) : (
                        <button type="button" className="admin-support-reopen" onClick={() => updateStatus("open")}>
                          Reopen
                        </button>
                      )
                    ) : (
                      <button type="button" className="admin-support-end" onClick={() => updateStatus("closed")}>
                        End conversation
                      </button>
                    )}
                  </>
                )}
                <button
                  type="button"
                  className="admin-support-delete"
                  onClick={deleteSelectedTicket}
                  disabled={deleting}
                >
                  {deleting ? "Deleting…" : "Delete"}
                </button>
              </header>
              <div className="admin-support-ticket-subject">
                <strong>{selectedTicket.Subject}</strong>
                <span>Received {formatDate(selectedTicket.CreatedAt)}</span>
              </div>
              <div className="admin-support-messages" ref={messageListRef}>
                <div className="admin-support-welcome">
                  <span>👋</span>
                  <strong>{selectedTicket.Archived ? "Archived conversation" : "Ticket conversation"}</strong>
                  <small>
                    {selectedTicket.Archived
                      ? "This read-only copy is stored in the admin archive vault."
                      : "Replies are emailed to the customer and saved to this ticket."}
                  </small>
                </div>
                {(selectedTicket.Messages || []).map((message) => (
                  <div
                    key={message.SupportTicketMessageID}
                    className={`admin-support-message ${message.SenderType === "admin" || message.SenderType === "system" ? "from-admin" : "from-client"}`}
                  >
                    {message.SenderType !== "admin" && (
                      <span className="admin-support-message-avatar">
                        {message.SenderType === "system" ? "✨" : "🙂"}
                      </span>
                    )}
                    <div className="admin-support-bubble">
                      <small>{message.SenderType === "admin" ? "Toora Flex team" : message.SenderName}</small>
                      <p>{message.Message}</p>
                      <time>{formatChatDateTime(message.CreatedAt)}</time>
                    </div>
                  </div>
                ))}
              </div>
              {!selectedTicket.Archived &&
                !["closed", "resolved"].includes(selectedTicket.Status) && (
                <form className="admin-support-reply" onSubmit={sendReply}>
                  <label htmlFor="quick-reply">Quick reply</label>
                  <select
                    id="quick-reply"
                    defaultValue=""
                    onChange={(event) => {
                      const preset = QUICK_REPLIES[Number(event.target.value)];
                      if (preset) setReply(preset.message);
                      event.target.value = "";
                    }}
                  >
                    <option value="">Choose a message to start with…</option>
                    {QUICK_REPLIES.map((preset, index) => (
                      <option value={index} key={preset.label}>{preset.label}</option>
                    ))}
                  </select>
                  <div className="admin-support-reply-row">
                    <textarea
                      value={reply}
                      onChange={(event) => setReply(event.target.value)}
                      placeholder="Write a reply…"
                      rows={3}
                      maxLength={5000}
                    />
                    <button type="submit" disabled={!reply.trim() || sending}>
                      {sending ? "Sending…" : "Send reply"}
                      <i className="fas fa-paper-plane" />
                    </button>
                  </div>
                </form>
              )}
            </>
          ) : (
            <div className="admin-support-empty admin-support-select-empty">
              <i className="far fa-comment-dots" />
              <strong>Select a support request</strong>
              <span>Customer details and reply controls will appear here.</span>
            </div>
          )}
        </section>
      </section>
      <Modal
        isOpen={Boolean(confirmAction)}
        onClose={() => !deleting && setConfirmAction("")}
        title={confirmAction.startsWith("archive")
          ? "Archive selected conversations?"
          : "Delete selected conversations?"}
        size="sm"
        showCloseButton={!deleting}
      >
        <div className="admin-support-confirm-content">
          <p>
            {confirmAction.startsWith("archive")
              ? `${selectedIds.length} conversation(s) will move to the searchable archive vault and leave the active inbox.`
              : confirmAction === "delete-selected"
                ? `Permanently delete ${selectedTicket?.TicketNumber || "this conversation"} and its messages? This cannot be undone.`
                : `Permanently delete ${selectedIds.length} selected conversation(s) and their messages? This cannot be undone.`}
          </p>
          <div className="admin-support-confirm-actions">
            <button
              type="button"
              onClick={() => setConfirmAction("")}
              disabled={deleting || processingBulkAction}
            >
              Cancel
            </button>
            <button
              type="button"
              className={confirmAction.startsWith("archive") ? "" : "danger"}
              onClick={runConfirmedAction}
              disabled={deleting || processingBulkAction}
            >
              {deleting || processingBulkAction
                ? "Working…"
                : confirmAction.startsWith("archive")
                  ? "Archive"
                  : "Delete permanently"}
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={Boolean(exportPassword)}
        onClose={() => {
          setPasswordCopied(false);
        }}
        closeOnOverlayClick={false}
        showCloseButton={false}
        title="Export downloaded — save this password"
        size="sm"
      >
        <div className="admin-support-password-content">
          <p>
            This password is shown only once and is not stored by Toora Flex.
            Save it securely; it is required to open the ZIP file.
            Use 7-Zip or WinZip to open the AES-256 encrypted archive.
          </p>
          <code>{exportPassword}</code>
          <div className="admin-support-confirm-actions">
            <button type="button" onClick={copyExportPassword}>
              {passwordCopied ? "Copied" : "Copy password"}
            </button>
            <button
              type="button"
              onClick={() => {
                setExportPassword("");
                setPasswordCopied(false);
              }}
            >
              I saved it
            </button>
          </div>
        </div>
      </Modal>
    </main>
  );
};

export default AdminSupportTickets;
